import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { FileText, Eye, CheckCircle, XCircle, Clock, PauseCircle, Ban, Search, Calendar, FileSignature, Loader2, Download } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import SignatureBlock from './SignatureBlock';
import ConventionDownload from './ConventionDownload';
import { ConventionSignatureModal } from './ConventionSignatureModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


export default function MesConventions({ userType: userType }) {
  const { user, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [conventions, setContrats] = useState([]);
  const [statutFilter, setStatutFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modals
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [openSignatureModal, setOpenSignatureModal] = useState(false);
  const [selectedConvention, setSelectedContrat] = useState(null);
  
  // Signature
  const [signatureData, setSignatureData] = useState({
    signature_electronique: '',
    accepte_conditions: false
  });

  useEffect(() => {
    if (user?.id) {
      fetchContrats();
    }
  }, [user]);

  const fetchContrats = async () => {
    setLoading(true);
    try {
      // Utiliser le nouvel endpoint qui inclut les infos de paiement
      const response = await axios.get(
        `${API}/convention-payments/mes-conventions`,
        { headers: getAuthHeader() }
      );
      setContrats(response.data.conventions || []);
    } catch {
      // Fallback vers l'ancien endpoint
      try {
        const fallbackResponse = await axios.get(
          `${API}/conventions/mes-conventions?user_id=${user.id}`,
          { headers: getAuthHeader() }
        );
        setContrats(fallbackResponse.data.conventions || []);
      } catch {
        toast.error('Erreur lors du chargement des conventions');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSigner = async () => {
    if (!signatureData.signature_electronique) {
      toast.error('Veuillez saisir votre nom complet pour la signature');
      return;
    }
    
    if (!signatureData.accepte_conditions) {
      toast.error('Vous devez accepter les conditions du convention');
      return;
    }
    
    try {
      await axios.post(
        `${API}/conventions/${selectedConvention.id}/signer`,
        {
          ...signatureData,
          ip_address: null
        },
        { 
          headers: getAuthHeader(),
          params: { user_id: user.id }
        }
      );
      
      toast.success('✅ Convention signé avec succès !');
      setOpenSignatureModal(false);
      resetSignatureData();
      fetchContrats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la signature');
    }
  };

  const resetSignatureData = () => {
    setSignatureData({
      signature_electronique: '',
      accepte_conditions: false
    });
  };

  const getStatutBadge = (statut) => {
    const badges = {
      brouillon: { bg: 'bg-gray-700', icon: <FileText className="h-3 w-3" />, label: 'Brouillon' },
      en_attente_signature: { bg: 'bg-orange-600', icon: <Clock className="h-3 w-3" />, label: 'En attente de signature' },
      signe: { bg: 'bg-blue-700', icon: <FileSignature className="h-3 w-3" />, label: 'Signé' },
      actif: { bg: 'bg-green-700', icon: <CheckCircle className="h-3 w-3" />, label: 'Actif' },
      suspendu: { bg: 'bg-red-700', icon: <PauseCircle className="h-3 w-3" />, label: 'Suspendu' },
      resilie: { bg: 'bg-red-900', icon: <Ban className="h-3 w-3" />, label: 'Résilié' },
      expire: { bg: 'bg-gray-700', icon: <XCircle className="h-3 w-3" />, label: 'Expiré' },
      archive: { bg: 'bg-slate-700', icon: <Download className="h-3 w-3" />, label: 'Archivé' }
    };
    
    const badge = badges[statut] || badges.brouillon;
    return (
      <Badge className={`${badge.bg} text-white`}>
        {badge.icon}
        <span className="ml-1">{badge.label}</span>
      </Badge>
    );
  };

  const getFilteredContrats = () => {
    let filtered = [...conventions];
    
    if (searchQuery) {
      filtered = filtered.filter(c =>
        c.titre?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.numero_contrat?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    if (statutFilter !== 'all') {
      filtered = filtered.filter(c => c.statut === statutFilter);
    }
    
    return filtered;
  };

  const filteredConventions = getFilteredContrats();
  const contratsEnAttente = conventions.filter(c => c.statut === 'en_attente_signature').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <Card className="bg-gradient-to-r from-purple-600 to-blue-600 text-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <FileText className="h-6 w-6" />
            Mes Conventions de Partenariat
          </CardTitle>
          <CardDescription className="text-white/90">
            Consultez et signez vos conventions
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Statistiques rapides */}
      {contratsEnAttente > 0 && (
        <Card className="border-2 border-orange-400 bg-orange-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Clock className="h-8 w-8 text-orange-600" />
              <div>
                <p className="text-2xl font-bold text-orange-900">{contratsEnAttente}</p>
                <p className="text-sm text-orange-700">Convention(s) en attente de signature</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filtres */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Rechercher un convention..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            <Select value={statutFilter} onValueChange={setStatutFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="en_attente_signature">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-amber-500" />
                    En attente
                  </span>
                </SelectItem>
                <SelectItem value="actif">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                    Actif
                  </span>
                </SelectItem>
                <SelectItem value="suspendu">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-orange-500" />
                    Suspendu
                  </span>
                </SelectItem>
                <SelectItem value="resilie">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-red-500" />
                    Résilié
                  </span>
                </SelectItem>
                <SelectItem value="archive">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-gray-500" />
                    Archivé
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="mt-4 text-sm text-slate-600">
            <strong>{filteredConventions.length}</strong> convention(s) trouvé(s)
          </div>
        </CardContent>
      </Card>

      {/* Liste des conventions */}
      <Card>
        <CardContent className="pt-6">
          {filteredConventions.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-600 font-medium">Aucun convention trouvé</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredConventions.map((convention) => (
                <Card key={convention.id} className="border-2 hover:shadow-lg transition-all">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <FileSignature className="h-5 w-5 text-purple-600" />
                          <h3 className="font-bold text-lg">{convention.titre}</h3>
                          {getStatutBadge(convention.statut)}
                        </div>
                        
                        {/* Badges des dates */}
                        <div className="flex flex-wrap gap-2 mt-3 mb-3">
                          <Badge className="bg-slate-600 text-white border-slate-700 font-semibold">
                            <Calendar className="h-3 w-3 mr-1" />
                            Reçu: {new Date(convention.date_envoi || convention.created_at).toLocaleDateString('fr-FR')}
                          </Badge>
                          
                          {convention.date_signature && (
                            <Badge className="bg-green-600 text-white border-green-700 font-semibold">
                              <FileSignature className="h-3 w-3 mr-1" />
                              Signé: {new Date(convention.date_signature).toLocaleDateString('fr-FR')}
                            </Badge>
                          )}
                          
                          {convention.date_fin && (
                            <Badge className="bg-orange-600 text-white border-orange-700 font-semibold">
                              <Clock className="h-3 w-3 mr-1" />
                              Expire: {new Date(convention.date_fin).toLocaleDateString('fr-FR')}
                            </Badge>
                          )}
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="font-semibold text-slate-700">📋 N°:</span>
                            <span className="font-mono text-slate-600">{convention.numero_contrat}</span>
                          </div>
                          
                          <div className="flex items-center gap-2 text-sm">
                            <span className="font-semibold text-slate-700">📅 Durée:</span>
                            <span className="text-slate-600">{convention.duree_mois} mois</span>
                          </div>

                          {convention.montant_mensuel && (
                            <div className="flex items-center gap-2 text-sm">
                              <span className="font-semibold text-slate-700">💰 Montant:</span>
                              <span className="text-slate-600">{convention.montant_mensuel} € / mois</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2 flex-wrap justify-end mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedContrat(convention);
                          setOpenDetailModal(true);
                        }}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Voir la convention
                      </Button>

                      {convention.statut === 'en_attente_signature' && (
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => {
                            setSelectedContrat(convention);
                            setOpenSignatureModal(true);
                          }}
                        >
                          <FileSignature className="h-4 w-4 mr-1" />
                          Signer la convention
                        </Button>
                      )}

                      {/* Boutons téléchargement pour conventions signées */}
                      {(convention.statut === 'signe' || convention.statut === 'actif') && (
                        <ConventionDownload convention={convention} variant="compact" />
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Détail */}
      <Dialog open={openDetailModal} onOpenChange={setOpenDetailModal}>
        <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-slate-600" />
              Détails de la convention
            </DialogTitle>
          </DialogHeader>

          {selectedConvention && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-3">
                <FileText className="h-6 w-6 text-purple-600" />
                <div className="flex-1">
                  <h3 className="font-bold text-lg">{selectedConvention.titre}</h3>
                  <p className="text-sm text-slate-600">{selectedConvention.numero_contrat}</p>
                </div>
                {getStatutBadge(selectedConvention.statut)}
              </div>

              {/* Timeline */}
              <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200">
                <CardContent className="pt-6">
                  <div className="space-y-3">
                    <div className="relative pl-6">
                      <div className="absolute left-2 top-2 bottom-2 w-0.5 bg-blue-300"></div>
                      
                      {selectedConvention.date_envoi && (
                        <div className="relative mb-3">
                          <div className="absolute left-[-1.375rem] w-4 h-4 rounded-full bg-blue-700 border-2 border-white"></div>
                          <div className="text-sm">
                            <p className="font-semibold text-blue-900">
                              Reçu le {new Date(selectedConvention.date_envoi).toLocaleDateString('fr-FR', { 
                                day: '2-digit', 
                                month: 'long', 
                                year: 'numeric' 
                              })}
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {selectedConvention.date_signature && (
                        <div className="relative mb-3">
                          <div className="absolute left-[-1.375rem] w-4 h-4 rounded-full bg-green-700 border-2 border-white"></div>
                          <div className="text-sm">
                            <p className="font-semibold text-green-900">
                              ✍️ Signé le {new Date(selectedConvention.date_signature).toLocaleDateString('fr-FR', { 
                                day: '2-digit', 
                                month: 'long', 
                                year: 'numeric' 
                              })}
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {selectedConvention.date_debut && (
                        <div className="relative">
                          <div className="absolute left-[-1.375rem] w-4 h-4 rounded-full bg-green-600 border-2 border-white"></div>
                          <div className="text-sm">
                            <p className="font-semibold text-green-900">
                              Actif depuis le {new Date(selectedConvention.date_debut).toLocaleDateString('fr-FR')}
                            </p>
                            {selectedConvention.date_fin && (
                              <p className="text-xs text-orange-600">
                                ⏰ Expire le {new Date(selectedConvention.date_fin).toLocaleDateString('fr-FR')}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Informations */}
              <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded">
                <div>
                  <Label className="text-xs text-slate-600">Durée</Label>
                  <p className="font-semibold">{selectedConvention.duree_mois} mois</p>
                </div>
                {selectedConvention.montant_mensuel && (
                  <div>
                    <Label className="text-xs text-slate-600">Montant mensuel</Label>
                    <p className="font-semibold">{selectedConvention.montant_mensuel} €</p>
                  </div>
                )}
              </div>

              {/* Contenu - AFFICHAGE COMPLET */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="font-bold">Contenu de la convention</Label>
                  <Badge className="bg-slate-600 text-white font-semibold">Document complet</Badge>
                </div>
                <div
                  className="border-2 rounded-lg p-6 bg-white prose prose-sm max-w-none"
                  style={{ maxHeight: 'none', minHeight: '300px' }}
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedConvention.contenu_html) }}
                />
              </div>

              {selectedConvention.conditions_specifiques && (
                <div>
                  <Label className="font-bold mb-2 block">Conditions spécifiques</Label>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap bg-yellow-50 p-4 rounded border border-yellow-200">
                    {selectedConvention.conditions_specifiques}
                  </p>
                </div>
              )}

              {/* Bloc de signature avec tampon COPPAM */}
              <SignatureBlock 
                convention={selectedConvention}
                isSignable={selectedConvention.statut === 'en_attente_signature'}
                onSign={async () => {
                  try {
                    await axios.post(
                      `${API}/conventions/${selectedConvention.id}/signer`,
                      {
                        signature_electronique: user?.prenom + ' ' + user?.nom,
                        accepte_conditions: true,
                        ip_address: null
                      },
                      { 
                        headers: getAuthHeader(),
                        params: { user_id: user.id }
                      }
                    );
                    
                    toast.success('✅ Convention signé avec succès !');
                    setOpenDetailModal(false);
                    fetchContrats();
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Erreur lors de la signature');
                  }
                }}
              />

              {/* Section téléchargement pour conventions signées */}
              {(selectedConvention.statut === 'signe' || selectedConvention.statut === 'actif') && (
                <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
                  <h4 className="font-bold text-green-800 mb-3 flex items-center gap-2">
                    <CheckCircle className="h-5 w-5" />
                    Votre convention signée
                  </h4>
                  <p className="text-sm text-green-700 mb-4">
                    Vous pouvez télécharger et conserver une copie de votre convention signée.
                  </p>
                  <ConventionDownload convention={selectedConvention} />
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDetailModal(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Signature (legacy - peut être supprimé) */}
      <ConventionSignatureModal
        open={openSignatureModal}
        onOpenChange={setOpenSignatureModal}
        convention={selectedConvention}
        signatureData={signatureData}
        setSignatureData={setSignatureData}
        resetSignatureData={resetSignatureData}
        onSign={handleSigner}
      />
    </div>
  );
}
